import Loading from '@/components/Loading';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { errorToast, successToast } from '@/helpers';
import InVivoError from '@/model/InVivoError';
import type { UserGroupRow } from '@/model/UserGroups.model';
import { ApiService, ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type FC, useState } from 'react';
import styles from './DeleteUserGroup.module.scss';

export const DELETE_CONFIRMATION_TEXT = 'DELETE';

interface DeleteUserGroupModalProps {
  closeModal: () => void;
  group: UserGroupRow;
  onDeleted: () => void;
}

const countOf = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

/**
 * Confirms deleting a single user group. Shows the studies and tasks that lose the access the group
 * granted, and only enables the delete once that impact has loaded and the administrator has typed
 * DELETE. The group's users are never deleted.
 */
const DeleteUserGroupModal: FC<DeleteUserGroupModalProps> = ({ closeModal, group, onDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/user-groups/{userGroupId}/impact',
    path: { userGroupId: group.api_id },
    options: { invokeOnInit: true, onError: { toast: false, capture: true, throw: false } },
  });

  const impact = response?.type === ResponseType.Success ? response.body : undefined;
  const impactFailed = response?.type === ResponseType.Error;
  const hasImpact = (impact?.study_count ?? 0) > 0 || (impact?.task_count ?? 0) > 0;
  const confirmed = confirmation.trim() === DELETE_CONFIRMATION_TEXT;
  const canDelete = impact !== undefined && confirmed && !deleting;

  const onDelete = async () => {
    setDeleting(true);
    const deleteResponse = await ApiService.call({
      endpoint: 'DELETE /api/v1/user-groups/{userGroupId}',
      path: { userGroupId: group.api_id },
      options: { onError: { toast: false, capture: false, throw: false } },
    });

    if (deleteResponse.type === ResponseType.Error) {
      errorToast('Could not delete the group');
      ExceptionHandler.captureException(
        new InVivoError('Could not delete user group', {
          cause: deleteResponse.error,
          slug: 'user-group-delete',
        })
      );
      setDeleting(false);
      return;
    }

    successToast(`Deleted group ${group.name}`);
    onDeleted();
    closeModal();
  };

  return (
    <div className="center mw7 ui-card" data-test-component="DeleteUserGroupModal" data-test-element="container">
      <div className="pa4" data-test-component="DeleteUserGroupModal" data-test-element="header">
        <h3 className="normal f4" data-testid="title">
          Delete group
        </h3>
      </div>
      <div className="ph4 pb4" data-test-component="DeleteUserGroupModal" data-test-element="body">
        {loading && impact === undefined && !impactFailed && <Loading txt="Checking what this group is used for" />}
        {impactFailed && (
          <Banner critical dismiss={false} testId="impact-error">
            <p className="f6 lh-copy">Could not check which studies and tasks use this group. Try again later.</p>
          </Banner>
        )}
        {impact !== undefined && (
          <>
            <Banner critical dismiss={false} testId="impact-warning">
              <p className="f6 lh-copy" data-testid="impact-summary">
                This action can&apos;t be undone.
                {hasImpact &&
                  ` This impacts ${countOf(impact.study_count, 'study', 'studies')} and ${countOf(impact.task_count, 'task', 'tasks')}.`}
              </p>
            </Banner>
            {impact.study_count > 0 && (
              <div className="mt3" data-test-element="affected-studies">
                <p className="f6 near-black pb2">Affected studies:</p>
                <ul className={styles.studies} data-testid="affected-studies-list">
                  {impact.studies.map((study) => (
                    <li key={study.api_id} className="f6 pv1" data-test-element="study" data-test-key={study.api_id}>
                      {study.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <label className="db mt3" htmlFor="delete-user-group-confirmation">
              <span className="db f6 near-black pb2">Type {DELETE_CONFIRMATION_TEXT}</span>
              <input
                id="delete-user-group-confirmation"
                type="text"
                autoComplete="off"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                disabled={deleting}
                data-testid="confirmation-input"
              />
            </label>
          </>
        )}
      </div>
      <div
        className="bt b--moon-gray bg-white pa3"
        data-test-component="DeleteUserGroupModal"
        data-test-element="footer"
      >
        <Button critical className="mr2" disabled={!canDelete} onClick={onDelete} testId="delete-button">
          {deleting ? 'Deleting…' : 'Delete group'}
        </Button>
        <Button plain onClick={closeModal} disabled={deleting} testId="close-button">
          Close
        </Button>
      </div>
    </div>
  );
};

export default DeleteUserGroupModal;
